import { ClientError } from '~/error/ClientError';
import { getMockPosition, resetMockPosition } from '~/composables/geolocations/mock/useGeolocationMock';
import { MessageCodeEnum } from '~/constants/MessageCode';
import { useSystemConfig } from '~/composables/common/useSystemConfig';
import type {
  GeolocationAcquisitionError,
  GeolocationSubscriberFeature,
} from '~/stores/geolocations/geolocationStore';

/**
 * 一回取得（getCurrentPosition）でキャッシュ位置を許容する最大有効期間（ミリ秒）。
 *
 * キャッシュ許容期間はwatchPosition取得済み位置の流用判定と密結合のため、
 * システム設定には出さず固定値で管理する。
 */
const ONE_SHOT_MAXIMUM_AGE = 30000;

/** モックモード時の位置情報配信間隔（ミリ秒） */
const MOCK_INTERVAL_MS = 1000;

/**
 * アプリ全体で共有する watchPosition の識別子。
 *
 * 複数のcomposableから requestWatch / releaseWatch を呼ばれても1つのwatchPositionで賄う。
 */
let watchId: number | null = null;

/** watchPosition起動処理が進行中かどうか（システム設定取得のawait中の多重起動防止） */
let isStartingWatch = false;

/** モックモード時に位置情報を定期配信する interval の識別子 */
let mockIntervalId: number | null = null;

/**
 * 位置情報取得APIをラップしたcomposable。
 *
 * - 一回限りの取得 (getPosition / checkGeolocationApiAvailable) を提供する。
 * - 継続取得 (watchPosition) のライフサイクルを参照カウント方式で管理し、結果をstoreに反映する。
 */
export const useGeoLocations = () => {
  const isGpsMockEnabled = parsePublicBoolean(useRuntimeConfig().public.gpsUseMock);
  const store = useGeolocationStore();
  const { getGpsAcquisitionSettings } = useSystemConfig('0-00');

  /** 一回限りの位置情報取得で発生したエラー情報 */
  const oneShotError = ref<GeolocationAcquisitionError | null>(null);

  /** ブラウザがGeolocation APIをサポートしているか */
  const isSupported = computed(() =>
    typeof navigator !== 'undefined' && 'geolocation' in navigator,
  );

  /**
   * 位置情報のエラーが権限関連かどうかを判定
   */
  const isPermissionError = computed(() =>
    oneShotError.value?.type === 'PERMISSION_DENIED',
  );

  /** 共有watchPositionで取得済みの位置情報が有効期限内であれば返す */
  const getAvailableWatchedPosition = (maximumAge: number) => {
    if (
      store.currentPosition !== null
      && store.lastUpdateAt !== null
      && Date.now() - store.lastUpdateAt <= maximumAge
      && store.acquisitionError === null
    ) {
      return store.currentPosition;
    }

    return null;
  };

  /** ブラウザで位置情報権限が明示的に拒否されていないかを確認する */
  const canUseCachedPosition = async () => {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
      return true;
    }

    try {
      const permissionStatus = await navigator.permissions.query({
        name: 'geolocation' as PermissionName,
      });
      return permissionStatus.state !== 'denied';
    }
    catch {
      return true;
    }
  };

  /**
   * 位置情報を一回だけ取得する
   *
   * タイムアウトはシステム設定（one-shot-timeout）、キャッシュ許容期間は ONE_SHOT_MAXIMUM_AGE を使用する。
   *
   * @param displayId 画面ID（ロギング/エラー用）
   * @returns 位置情報取得のPromise
   */
  const getPosition = async (displayId?: string): Promise<GeolocationPosition> => {
    const logger = useLogger(displayId ?? '0-00');

    // モック用: ダミーデータを返す
    if (isGpsMockEnabled) {
      return getMockPosition();
    }

    if (!isSupported.value) {
      logger.warn('ブラウザがGeolocation APIをサポートしていません。');
      return Promise.reject(new ClientError(MessageCodeEnum.LOCATION_SERVICE_NOT_AVAILABLE, displayId ?? '0-00'));
    }

    oneShotError.value = null;
    const { oneShotTimeout: timeout } = await getGpsAcquisitionSettings();
    const maximumAge = ONE_SHOT_MAXIMUM_AGE;

    if (await canUseCachedPosition()) {
      const watchedPosition = getAvailableWatchedPosition(maximumAge);
      if (watchedPosition !== null) {
        return watchedPosition;
      }
    }

    return new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          oneShotError.value = null;
          resolve(position);
        },
        (err) => {
          oneShotError.value = _mapGeolocationError(err);
          reject(new ClientError(MessageCodeEnum.LOCATION_SERVICE_NOT_AVAILABLE, displayId ?? '0-00'));
        },
        {
          enableHighAccuracy: true,
          timeout,
          maximumAge,
        },
      );
    });
  };

  /**
   * Geolocation APIが使用可能かチェックする
   *
   * 以下の場合にエラーとなる想定
   * - ブラウザがGeolocation APIをサポートしていない
   * - ブラウザの設定で位置情報の取得を許可していない
   *
   * @param displayId 画面ID
   * @returns true: 使用可能、false: 使用不可
   */
  const checkGeolocationApiAvailable = async (displayId: string) => {
    try {
      await getPosition(displayId);
    }
    catch {
      return false;
    }
    return true;
  };

  /**
   * 位置情報の継続取得を要求する。
   *
   * 1人目の購読者で watchPosition を起動する。複数人目以降は購読者リストに加わるのみ。
   * 同じ feature を二重に request しても問題ない（Set による冪等性）。
   */
  const requestWatch = (feature: GeolocationSubscriberFeature) => {
    store.addSubscriber(feature);
    if (watchId === null && mockIntervalId === null) {
      _startWatchPosition();
    }
  };

  /**
   * 位置情報の継続取得を解除する。
   *
   * 購読者が0になった場合 watchPosition を停止し生位置をクリアする。
   */
  const releaseWatch = (feature: GeolocationSubscriberFeature) => {
    store.removeSubscriber(feature);
    if (store.hasNoSubscribers) {
      _stopWatchPosition();
      store.clearPositionData();
    }
  };

  /** watchPosition または mock interval を開始する */
  const _startWatchPosition = () => {
    if (isGpsMockEnabled) {
      resetMockPosition();
      const tick = () => store.applyPositionUpdate(getMockPosition());
      tick();
      mockIntervalId = window.setInterval(tick, MOCK_INTERVAL_MS);
      return;
    }

    if (!isSupported.value) {
      store.setAcquisitionError({
        type: 'UNSUPPORTED',
        message: 'ブラウザがGeolocation APIをサポートしていません。',
      });
      return;
    }

    void _startRealWatchPosition();
  };

  /**
   * システム設定からタイムアウトを取得して watchPosition を開始する。
   *
   * 設定取得のawait中に再度呼ばれた場合は多重起動せず、
   * await中に全購読者が解除された場合は起動しない。
   */
  const _startRealWatchPosition = async () => {
    if (isStartingWatch) {
      return;
    }
    isStartingWatch = true;
    try {
      const { watchTimeout } = await getGpsAcquisitionSettings();
      if (watchId !== null || store.hasNoSubscribers) {
        return;
      }
      watchId = navigator.geolocation.watchPosition(
        (pos) => store.applyPositionUpdate(pos),
        (err) => store.setAcquisitionError(_mapGeolocationError(err)),
        // 継続取得のため maximumAge=0 で最新を取得
        { enableHighAccuracy: true, timeout: watchTimeout, maximumAge: 0 },
      );
    }
    finally {
      isStartingWatch = false;
    }
  };

  /** watchPosition または mock interval を停止する */
  const _stopWatchPosition = () => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }
    if (mockIntervalId !== null) {
      window.clearInterval(mockIntervalId);
      mockIntervalId = null;
    }
  };

  return {
    isSupported,
    isPermissionError,
    getPosition,
    checkGeolocationApiAvailable,
    requestWatch,
    releaseWatch,
    resetMockPosition,
  };
};

/** GeolocationPositionError をストア用のエラー情報に変換する */
const _mapGeolocationError = (err: GeolocationPositionError): GeolocationAcquisitionError => {
  switch (err.code) {
    case err.PERMISSION_DENIED:
      return { code: err.code, type: 'PERMISSION_DENIED', message: '位置情報の使用許可が拒否されました' };
    case err.POSITION_UNAVAILABLE:
      return { code: err.code, type: 'POSITION_UNAVAILABLE', message: '位置情報を取得できませんでした' };
    case err.TIMEOUT:
      return { code: err.code, type: 'TIMEOUT', message: '位置情報の取得がタイムアウトしました' };
    default:
      return { code: err.code, type: 'UNKNOWN', message: '不明なエラーが発生しました' };
  }
};
